import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import client, { apiBaseURL } from "../api/client";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const nextPath = useMemo(() => {
    const queryNext = new URLSearchParams(location.search).get("next");
    return queryNext ? decodeURIComponent(queryNext) : "/tools/wpa/dashboard";
  }, [location.search]);

  const formatLoginError = (e) => {
    const timeoutMs = Number(e?.config?.timeout || 0);
    const timeoutSeconds = timeoutMs > 0 ? Math.round(timeoutMs / 1000) : 15;
    const status = Number(e?.response?.status || 0);
    const apiMessage = e?.response?.data?.message;

    if (e?.code === "ECONNABORTED") {
      return `Login request timed out after ${timeoutSeconds}s. API is reachable but login did not complete. Check backend logs and database connection.`;
    }

    if (e?.code === "ERR_NETWORK") {
      return `Cannot reach API server at ${apiBaseURL}. Make sure backend is running and CORS/base URL are correct.`;
    }

    if (status >= 500) {
      return apiMessage || "Backend error during login (5xx). This is usually API/database-side. Check backend logs.";
    }

    if (status === 401) {
      return apiMessage || "Invalid email or password.";
    }

    if (status >= 400) {
      return apiMessage || `Login failed with status ${status}.`;
    }

    return e?.message || "Login failed";
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await client.post("/auth/login", form);
      const { data } = response;

      if (!data?.token || !data?.user) {
        const contentType = response?.headers?.["content-type"] || "";
        const preview = typeof data === "string" ? data.slice(0, 120) : JSON.stringify(data || {}).slice(0, 120);
        throw new Error(`Unexpected login response from API (content-type: ${contentType || "unknown"}). Preview: ${preview}`);
      }

      login(data.token, data.user);
      navigate(nextPath);
    } catch (e) {
      setError(formatLoginError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-shell">
      <section className="login-surface">
        <div className="login-left">
          <p className="landing-eyebrow">Secure Access</p>
          <h2>Welcome Back</h2>
          <p>Sign in to continue with website stability reviews, incident capture, and structured previews.</p>

          <form onSubmit={submit}>
            <input
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <button type="submit" disabled={loading}>{loading ? "Signing In..." : "Sign In"}</button>
          </form>

          {error ? <p className="error">{error}</p> : null}
          <Link to="/" className="back-link">Back to landing page</Link>
        </div>

        <aside className="login-right">
          <h3>Assessment Principles</h3>
          <ul>
            <li>Separate symptoms from root causes</li>
            <li>Connect technical issues to business impact</li>
            <li>Document evidence before recommendations</li>
            <li>Prioritize practical next steps</li>
          </ul>
        </aside>
      </section>
    </main>
  );
}
